import React, { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Modal,
  MultiSelect,
  NumberInput,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { IconEdit, IconPlus, IconTrash } from '@tabler/icons-react';
import { modals } from '@mantine/modals';
import {
  ClinicalNoteType,
  IScenarioClinicalNoteEntry,
  IScenarioClinicalNoteEntryWrite,
  IScenarioDetail,
  IScenarioJournalEntry,
  IScenarioJournalEntryWrite,
  IScenarioDocumentEntry,
  IScenarioLifeEventEntry,
  IScenarioPrescriptionEntry,
  IScenarioPatientSlot,
  IScenarioPhysiologicalEntry,
  IScenarioPhysiologicalEntryWrite,
  IScenarioQuestionnaireEntry,
  IScenarioQuestionnaireEntryWrite,
} from '../../../global/entities/Scenario/Scenario.ts';
import {
  useCreateScenarioSlot,
  useDeleteScenarioSlot,
  useUpdateScenarioSlot,
  useCreateQuestionnaireEntry,
  useUpdateQuestionnaireEntry,
  useDeleteQuestionnaireEntry,
  useCreatePhysiologicalEntry,
  useUpdatePhysiologicalEntry,
  useDeletePhysiologicalEntry,
  useCreateJournalEntry,
  useUpdateJournalEntry,
  useDeleteJournalEntry,
  useCreateClinicalNoteEntry,
  useUpdateClinicalNoteEntry,
  useDeleteClinicalNoteEntry,
  useScenarioLookups,
} from '../../../global/entities/Scenario/queries/useScenarios.ts';
import { useGetQuestionnairesByIdParam } from '../../../global/entities/Questionnaires/queries/useGetQuestionnairesByIdParam.tsx';
import { useJournal } from '../../../global/entities/Journal/queries/useJournals.ts';
import {
  IJournalQuestion,
  IMultiSelectOptions,
} from '../../../global/entities/Journal/Journal.ts';

interface IProps {
  scenario: IScenarioDetail;
  readOnly?: boolean;
}

interface IDayRow {
  offsetDays: number;
  timeOfDay: string | null;
}

function dayKey(d: { offsetDays: number; timeOfDay: string | null }): string {
  return `${d.offsetDays}|${d.timeOfDay ?? ''}`;
}

function sameDay(
  a: { offsetDays: number; timeOfDay: string | null },
  b: { offsetDays: number; timeOfDay: string | null },
): boolean {
  return (
    a.offsetDays === b.offsetDays &&
    (a.timeOfDay ?? null) === (b.timeOfDay ?? null)
  );
}

// .NET TimeSpan? deserializer requires strict "HH:mm:ss" / "HH:mm:ss.fff"
// — normalize anything looser ("8", "8:30", "08:30") that the user might type.
function normalizeTimeOfDay(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const parts = trimmed.split(':');
  if (parts.length < 1 || parts.length > 3) return null;
  const nums = parts.map((p) => Number(p));
  if (nums.some((n) => Number.isNaN(n))) return null;
  const [h, m = 0, s = 0] = nums;
  if (h < 0 || h > 23 || m < 0 || m > 59 || s < 0 || s > 59) return null;
  const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

const ScenarioSlotsEditor: React.FC<IProps> = ({
  scenario,
  readOnly = false,
}) => {
  const { t } = useTranslation();

  const [activeSlotId, setActiveSlotId] = useState<string | null>(
    scenario.slots[0]?.id?.toString() ?? null,
  );

  useEffect(() => {
    if (!activeSlotId && scenario.slots[0])
      setActiveSlotId(scenario.slots[0].id.toString());
  }, [scenario.slots, activeSlotId]);

  const createSlot = useCreateScenarioSlot(scenario.id);

  const handleAddSlot = async () => {
    const id = await createSlot.mutateAsync({
      label: t('scenario.newSlotDefaultLabel'),
      description: '',
      medicalNote: null,
      orderIndex: scenario.slots.length,
    });
    setActiveSlotId(id.toString());
  };

  const activeSlot = useMemo(
    () => scenario.slots.find((s) => s.id.toString() === activeSlotId) ?? null,
    [activeSlotId, scenario.slots],
  );

  return (
    <Stack gap="md">
      <Group>
        <Tabs
          value={activeSlotId}
          onChange={setActiveSlotId}
          style={{ flex: 1 }}
        >
          <Tabs.List>
            {scenario.slots.map((slot) => (
              <Tabs.Tab key={slot.id} value={slot.id.toString()}>
                {slot.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>
        <Button
          leftSection={<IconPlus size={16} />}
          variant="light"
          onClick={handleAddSlot}
        >
          {t('scenario.addSlot')}
        </Button>
      </Group>

      {activeSlot && (
        <SlotPanel
          key={activeSlot.id}
          scenario={scenario}
          slot={activeSlot}
          onSlotDeleted={() => setActiveSlotId(null)}
          readOnly={readOnly}
        />
      )}

      {!activeSlot && scenario.slots.length === 0 && (
        <Text c="dimmed">{t('scenario.noSlotsYet')}</Text>
      )}
    </Stack>
  );
};

export default ScenarioSlotsEditor;

// ---------- Slot panel (owns shared day rows) ----------

interface ISlotPanelProps {
  scenario: IScenarioDetail;
  slot: IScenarioPatientSlot;
  onSlotDeleted: () => void;
  readOnly?: boolean;
}

const SlotPanel: React.FC<ISlotPanelProps> = ({
  scenario,
  slot,
  onSlotDeleted,
  readOnly = false,
}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('fr') ? 'fr' : 'en';
  const updateSlot = useUpdateScenarioSlot(scenario.id);
  const deleteSlot = useDeleteScenarioSlot(scenario.id);

  // All-category mutations needed when editing/deleting a day across the slot
  const updateQEntry = useUpdateQuestionnaireEntry(scenario.id);
  const deleteQEntry = useDeleteQuestionnaireEntry(scenario.id);
  const updatePhysio = useUpdatePhysiologicalEntry(scenario.id);
  const deletePhysio = useDeletePhysiologicalEntry(scenario.id);
  const updateJournal = useUpdateJournalEntry(scenario.id);
  const deleteJournal = useDeleteJournalEntry(scenario.id);
  const updateNote = useUpdateClinicalNoteEntry(scenario.id);
  const deleteNote = useDeleteClinicalNoteEntry(scenario.id);

  const [extraDays, setExtraDays] = useState<IDayRow[]>([]);

  const sortRows = (rows: IDayRow[]): IDayRow[] =>
    rows.sort((a, b) => {
      if (a.offsetDays !== b.offsetDays) return a.offsetDays - b.offsetDays;
      return (a.timeOfDay ?? '').localeCompare(b.timeOfDay ?? '');
    });

  const buildRows = (
    entries: { offsetDays: number; timeOfDay: string | null }[],
    includeExtra: boolean,
  ): IDayRow[] => {
    const map = new Map<string, IDayRow>();
    const insert = (offset: number, timeOfDay: string | null) => {
      const row: IDayRow = { offsetDays: offset, timeOfDay };
      const k = dayKey(row);
      if (!map.has(k)) map.set(k, row);
    };
    for (const e of entries) insert(e.offsetDays, e.timeOfDay);
    if (includeExtra)
      for (const d of extraDays) insert(d.offsetDays, d.timeOfDay);
    return sortRows([...map.values()]);
  };

  // Each section displays only the days that have data for that section, so a
  // 300-day journal import doesn't pad the questionnaire table with empty rows.
  // In editable mode (no Excel import) we still include extraDays so a manually-
  // added day appears in every tab — that's how the slot-level "Add day" flow works.
  const questionnaireRows = useMemo(
    () => buildRows(slot.questionnaireEntries, !readOnly),
    [slot.questionnaireEntries, extraDays, readOnly],
  );
  const physiologicalRows = useMemo(
    () => buildRows(slot.physiologicalEntries, !readOnly),
    [slot.physiologicalEntries, extraDays, readOnly],
  );
  const journalRows = useMemo(
    () => buildRows(slot.journalEntries, !readOnly),
    [slot.journalEntries, extraDays, readOnly],
  );
  const clinicalNoteRows = useMemo(
    () => buildRows(slot.clinicalNoteEntries, !readOnly),
    [slot.clinicalNoteEntries, extraDays, readOnly],
  );
  const lifeEventRows = useMemo(
    () => buildRows(slot.lifeEventEntries ?? [], !readOnly),
    [slot.lifeEventEntries, extraDays, readOnly],
  );
  const documentRows = useMemo(
    () => buildRows(slot.documentEntries ?? [], !readOnly),
    [slot.documentEntries, extraDays, readOnly],
  );

  // Union of every day across all sections — used by the slot-level "Add day"
  // collision detection and by handlers that need to know about all known days.
  const allDays = useMemo<IDayRow[]>(() => {
    const map = new Map<string, IDayRow>();
    const insert = (offset: number, timeOfDay: string | null) => {
      const row: IDayRow = { offsetDays: offset, timeOfDay };
      const k = dayKey(row);
      if (!map.has(k)) map.set(k, row);
    };
    for (const e of slot.questionnaireEntries)
      insert(e.offsetDays, e.timeOfDay);
    for (const e of slot.physiologicalEntries)
      insert(e.offsetDays, e.timeOfDay);
    for (const e of slot.journalEntries) insert(e.offsetDays, e.timeOfDay);
    for (const e of slot.clinicalNoteEntries) insert(e.offsetDays, e.timeOfDay);
    for (const e of slot.lifeEventEntries ?? [])
      insert(e.offsetDays, e.timeOfDay);
    for (const e of slot.documentEntries ?? [])
      insert(e.offsetDays, e.timeOfDay);
    for (const d of extraDays) insert(d.offsetDays, d.timeOfDay);
    return sortRows([...map.values()]);
  }, [slot, extraDays]);

  const handleRename = () => {
    let newLabel = slot.label;
    modals.open({
      title: t('scenario.renameSlot'),
      children: (
        <Stack>
          <TextInput
            label={t('formGlobal.name')}
            defaultValue={slot.label}
            onChange={(e) => (newLabel = e.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button variant="outline" onClick={() => modals.closeAll()}>
              {t('button.cancel')}
            </Button>
            <Button
              onClick={async () => {
                await updateSlot.mutateAsync({
                  slotId: slot.id,
                  model: {
                    label: newLabel,
                    description: slot.description,
                    medicalNote: slot.medicalNote,
                    orderIndex: slot.orderIndex,
                  },
                });
                modals.closeAll();
              }}
            >
              {t('button.save')}
            </Button>
          </Group>
        </Stack>
      ),
    });
  };

  const handleDelete = () =>
    modals.openConfirmModal({
      title: t('modal.deleteItemModal.title'),
      children: (
        <Text>{t('scenario.deleteSlotWarning', { label: slot.label })}</Text>
      ),
      labels: { confirm: t('button.delete'), cancel: t('button.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        await deleteSlot.mutateAsync(slot.id);
        onSlotDeleted();
      },
    });

  const handleAddDay = (offset: number, timeOfDay: string | null) => {
    const candidate: IDayRow = { offsetDays: offset, timeOfDay };
    if (allDays.some((d) => sameDay(d, candidate))) return;
    setExtraDays((prev) => [...prev, candidate]);
  };

  const handleEditDay = async (
    oldRow: IDayRow,
    newOffset: number,
    newTime: string | null,
  ) => {
    const newRow: IDayRow = { offsetDays: newOffset, timeOfDay: newTime };
    // If the target (offset, time) already has another row, abort the move
    // and keep the old key — avoids silently merging two distinct day rows.
    const collision =
      !sameDay(oldRow, newRow) && allDays.some((d) => sameDay(d, newRow));
    const target = collision ? oldRow : newRow;

    const matches = (e: { offsetDays: number; timeOfDay: string | null }) =>
      sameDay(e, oldRow);

    for (const e of slot.questionnaireEntries.filter(matches)) {
      await updateQEntry.mutateAsync({
        entryId: e.id,
        model: {
          questionnaireID: e.questionnaireID,
          offsetDays: target.offsetDays,
          timeOfDay: target.timeOfDay,
          scoresJson: e.scoresJson,
          answersJson: e.answersJson,
        },
      });
    }
    for (const e of slot.physiologicalEntries.filter(matches)) {
      await updatePhysio.mutateAsync({
        entryId: e.id,
        model: {
          physiologicalTypeID: e.physiologicalTypeID,
          unitID: e.unitID,
          offsetDays: target.offsetDays,
          timeOfDay: target.timeOfDay,
          value: e.value,
        },
      });
    }
    for (const e of slot.journalEntries.filter(matches)) {
      await updateJournal.mutateAsync({
        entryId: e.id,
        model: {
          journalID: e.journalID,
          offsetDays: target.offsetDays,
          timeOfDay: target.timeOfDay,
          responsesJson: e.responsesJson,
        },
      });
    }
    for (const e of slot.clinicalNoteEntries.filter(matches)) {
      await updateNote.mutateAsync({
        entryId: e.id,
        model: {
          noteTypeID: e.noteTypeID,
          questionnaireID: e.questionnaireID,
          offsetDays: target.offsetDays,
          timeOfDay: target.timeOfDay,
          freeTextNote: e.freeTextNote,
          answersJson: e.answersJson,
        },
      });
    }

    setExtraDays((prev) => {
      const mapped = prev.map((d) => (sameDay(d, oldRow) ? target : d));
      const seen = new Set<string>();
      return mapped.filter((d) => {
        const k = dayKey(d);
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
    });
  };

  const handleDeleteDay = async (row: IDayRow) => {
    const matches = (e: { offsetDays: number; timeOfDay: string | null }) =>
      sameDay(e, row);
    for (const e of slot.questionnaireEntries.filter(matches))
      await deleteQEntry.mutateAsync(e.id);
    for (const e of slot.physiologicalEntries.filter(matches))
      await deletePhysio.mutateAsync(e.id);
    for (const e of slot.journalEntries.filter(matches))
      await deleteJournal.mutateAsync(e.id);
    for (const e of slot.clinicalNoteEntries.filter(matches))
      await deleteNote.mutateAsync(e.id);
    setExtraDays((prev) => prev.filter((d) => !sameDay(d, row)));
  };

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Group>
          <Button size="xs" variant="outline" onClick={handleRename}>
            {t('scenario.renameSlot')}
          </Button>
          <Button
            size="xs"
            variant="outline"
            color="red"
            onClick={handleDelete}
          >
            {t('scenario.deleteSlot')}
          </Button>
        </Group>
        {!readOnly && (
          <AddDayButton
            existingOffsets={allDays.map((d) => d.offsetDays)}
            onAdd={handleAddDay}
          />
        )}
      </Group>

      <Textarea
        label={t('scenario.slotMedicalNote')}
        description={t('scenario.slotMedicalNoteHelp')}
        value={slot.medicalNote ?? ''}
        placeholder={t('scenario.slotMedicalNoteEmpty')}
        autosize
        minRows={2}
        readOnly
      />

      <Tabs defaultValue="questionnaires">
        <Tabs.List>
          <Tabs.Tab value="questionnaires">
            {t('scenario.tab.questionnaires')}
          </Tabs.Tab>
          <Tabs.Tab value="physiological">
            {t('scenario.tab.physiological')}
          </Tabs.Tab>
          <Tabs.Tab value="journals">{t('scenario.tab.journals')}</Tabs.Tab>
          <Tabs.Tab value="clinicalNotes">
            {t('scenario.tab.clinicalNotes')}
          </Tabs.Tab>
          <Tabs.Tab value="lifeEvents">{t('scenario.tab.lifeEvents')}</Tabs.Tab>
          <Tabs.Tab value="documents">{t('scenario.tab.documents')}</Tabs.Tab>
          <Tabs.Tab value="prescriptions">
            {t('scenario.tab.prescriptions')}
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="questionnaires" pt="md">
          <QuestionnaireSection
            scenario={scenario}
            slot={slot}
            rows={questionnaireRows}
            lang={lang}
            onEditDay={handleEditDay}
            onDeleteDay={handleDeleteDay}
            readOnly={readOnly}
          />
        </Tabs.Panel>
        <Tabs.Panel value="physiological" pt="md">
          <PhysiologicalGrid
            scenario={scenario}
            slot={slot}
            rows={physiologicalRows}
            onEditDay={handleEditDay}
            onDeleteDay={handleDeleteDay}
          />
        </Tabs.Panel>
        <Tabs.Panel value="journals" pt="md">
          <JournalGrid
            scenario={scenario}
            slot={slot}
            rows={journalRows}
            lang={lang}
            onEditDay={handleEditDay}
            onDeleteDay={handleDeleteDay}
            readOnly={readOnly}
          />
        </Tabs.Panel>
        <Tabs.Panel value="clinicalNotes" pt="md">
          <ClinicalNoteGrid
            scenario={scenario}
            slot={slot}
            rows={clinicalNoteRows}
            onEditDay={handleEditDay}
            onDeleteDay={handleDeleteDay}
          />
        </Tabs.Panel>
        <Tabs.Panel value="lifeEvents" pt="md">
          <LifeEventGrid slot={slot} rows={lifeEventRows} />
        </Tabs.Panel>
        <Tabs.Panel value="documents" pt="md">
          <DocumentGrid slot={slot} rows={documentRows} />
        </Tabs.Panel>
        <Tabs.Panel value="prescriptions" pt="md">
          <PrescriptionGrid slot={slot} />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
};

// ---------- Shared interfaces for grids ----------

interface IGridProps {
  scenario: IScenarioDetail;
  slot: IScenarioPatientSlot;
  rows: IDayRow[];
  onEditDay: (
    oldRow: IDayRow,
    newOffset: number,
    newTime: string | null,
  ) => Promise<void> | void;
  onDeleteDay: (row: IDayRow) => Promise<void> | void;
  readOnly?: boolean;
}

// ---------- Day controls ----------

const AddDayButton: React.FC<{
  existingOffsets: number[];
  onAdd: (offset: number, timeOfDay: string | null) => void;
}> = ({ existingOffsets, onAdd }) => {
  const { t } = useTranslation();
  const [opened, setOpened] = useState(false);
  const [offset, setOffset] = useState<number>(0);
  const [timeOfDay, setTimeOfDay] = useState<string>('');

  const open = () => {
    setOffset(existingOffsets.length ? Math.max(...existingOffsets) + 1 : 0);
    setTimeOfDay('');
    setOpened(true);
  };

  return (
    <>
      <Button
        size="xs"
        variant="light"
        leftSection={<IconPlus size={14} />}
        onClick={open}
      >
        {t('scenario.addDay')}
      </Button>
      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title={t('scenario.addDay')}
        size="sm"
      >
        <Stack>
          <NumberInput
            label={t('scenario.col.offsetDays')}
            value={offset}
            onChange={(v) => setOffset(Number(v) || 0)}
          />
          <TextInput
            label={t('scenario.col.time')}
            value={timeOfDay}
            onChange={(e) => setTimeOfDay(e.currentTarget.value)}
            placeholder="HH:mm:ss"
          />
          <Group justify="flex-end">
            <Button variant="outline" onClick={() => setOpened(false)}>
              {t('button.cancel')}
            </Button>
            <Button
              onClick={() => {
                onAdd(offset, normalizeTimeOfDay(timeOfDay));
                setOpened(false);
              }}
            >
              {t('button.add')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

const DayRowHeader: React.FC<{
  rank: number;
  row: IDayRow;
  onEditDay: (offset: number, timeOfDay: string | null) => void;
  onDeleteDay: () => void;
  readOnly?: boolean;
}> = ({ row, onEditDay, onDeleteDay, readOnly = false }) => {
  const { t } = useTranslation();
  const [opened, setOpened] = useState(false);
  const [offset, setOffset] = useState<number>(row.offsetDays);
  const [timeOfDay, setTimeOfDay] = useState<string>(row.timeOfDay ?? '');

  useEffect(() => {
    setOffset(row.offsetDays);
    setTimeOfDay(row.timeOfDay ?? '');
  }, [row.offsetDays, row.timeOfDay]);

  return (
    <Group gap={4} wrap="nowrap">
      <Stack gap={0}>
        <Text fw={600} fz="sm">
          {t('scenario.dayLabel', { n: row.offsetDays + 1 })}
        </Text>
        <Text c="dimmed" fz="xs">
          {t('scenario.col.offsetDays')}: {row.offsetDays}
          {row.timeOfDay ? ` · ${row.timeOfDay}` : ''}
        </Text>
      </Stack>
      {!readOnly && (
        <>
          <ActionIcon
            size="sm"
            variant="subtle"
            onClick={() => setOpened(true)}
          >
            <IconEdit size={14} />
          </ActionIcon>
          <ActionIcon
            size="sm"
            variant="subtle"
            color="red"
            onClick={onDeleteDay}
          >
            <IconTrash size={14} />
          </ActionIcon>
        </>
      )}
      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title={t('scenario.editDay')}
        size="sm"
      >
        <Stack>
          <NumberInput
            label={t('scenario.col.offsetDays')}
            value={offset}
            onChange={(v) => setOffset(Number(v) || 0)}
          />
          <TextInput
            label={t('scenario.col.time')}
            value={timeOfDay}
            onChange={(e) => setTimeOfDay(e.currentTarget.value)}
            placeholder="HH:mm:ss"
          />
          <Group justify="flex-end">
            <Button variant="outline" onClick={() => setOpened(false)}>
              {t('button.cancel')}
            </Button>
            <Button
              onClick={() => {
                onEditDay(offset, normalizeTimeOfDay(timeOfDay));
                setOpened(false);
              }}
            >
              {t('button.save')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Group>
  );
};

// ---------- Inline cells ----------

const NumericCell: React.FC<{
  value: number | null;
  compact?: boolean;
  readOnly?: boolean;
  onCommit: (next: number | null) => void;
}> = ({ value, compact, readOnly, onCommit }) => {
  const [local, setLocal] = useState<string>(
    value === null || value === undefined ? '' : value.toString(),
  );

  useEffect(() => {
    setLocal(value === null || value === undefined ? '' : value.toString());
  }, [value]);

  if (readOnly) {
    return (
      <Text size="xs" ta="right">
        {value === null || value === undefined ? '—' : value}
      </Text>
    );
  }

  const commit = () => {
    const trimmed = local.trim();
    if (trimmed === '') {
      if (value !== null && value !== undefined) onCommit(null);
      return;
    }
    const n = Number(trimmed);
    if (Number.isNaN(n)) {
      setLocal(value === null || value === undefined ? '' : value.toString());
      return;
    }
    if (n !== value) onCommit(n);
  };

  return (
    <TextInput
      value={local}
      onChange={(e) => setLocal(e.currentTarget.value)}
      onBlur={commit}
      size="xs"
      styles={{
        input: compact
          ? { textAlign: 'right', minWidth: 0, width: '100%', padding: '0 4px' }
          : { textAlign: 'right', minWidth: 70 },
      }}
    />
  );
};

const TextCell: React.FC<{
  value: string | null;
  placeholder?: string;
  readOnly?: boolean;
  onCommit: (next: string | null) => void;
}> = ({ value, placeholder, readOnly, onCommit }) => {
  const [local, setLocal] = useState<string>(value ?? '');

  useEffect(() => {
    setLocal(value ?? '');
  }, [value]);

  if (readOnly) 
    return <Text size="xs">{value ?? '—'}</Text>;

  const commit = () => {
    const next = local.length === 0 ? null : local;
    if (next !== value) onCommit(next);
  };

  return (
    <TextInput
      value={local}
      onChange={(e) => setLocal(e.currentTarget.value)}
      onBlur={commit}
      placeholder={placeholder}
      size="xs"
      styles={{ input: { minWidth: 140 } }}
    />
  );
};

// ---------- Questionnaire section: sub-tabs per questionnaire ----------

interface IQuestionnaireSectionProps extends IGridProps {
  lang: string;
}

const QuestionnaireSection: React.FC<IQuestionnaireSectionProps> = ({
  scenario,
  slot,
  rows,
  lang,
  onEditDay,
  onDeleteDay,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const [activeQID, setActiveQID] = useState<string | null>(
    scenario.questionnaires[0]?.questionnaireID?.toString() ?? null,
  );

  useEffect(() => {
    if (!activeQID && scenario.questionnaires[0])
      setActiveQID(scenario.questionnaires[0].questionnaireID.toString());
  }, [scenario.questionnaires, activeQID]);

  if (scenario.questionnaires.length === 0)
    {return (
      <Text c="dimmed" size="sm">
        {t('scenario.noTypesSelectedHint')}
      </Text>
    );}

  return (
    <Stack gap="sm">
      <Title order={5}>{t('scenario.questionnaires')}</Title>
      <Tabs value={activeQID} onChange={setActiveQID} variant="outline">
        <Tabs.List>
          {scenario.questionnaires.map((q) => (
            <Tabs.Tab key={q.id} value={q.questionnaireID.toString()}>
              {lang === 'fr' ? q.shortNameFr : q.shortNameEn}
            </Tabs.Tab>
          ))}
        </Tabs.List>
        {scenario.questionnaires.map((q) => (
          <Tabs.Panel key={q.id} value={q.questionnaireID.toString()} pt="md">
            <SingleQuestionnaireGrid
              scenario={scenario}
              slot={slot}
              rows={rows}
              questionnaireID={q.questionnaireID}
              onEditDay={onEditDay}
              onDeleteDay={onDeleteDay}
              readOnly={readOnly}
            />
          </Tabs.Panel>
        ))}
      </Tabs>
    </Stack>
  );
};

// "WEISS-P (Risky)" → "Risky"  ·  "Risky" → "Risky"
function subscaleLabel(key: string): string {
  const m = key.match(/^.*\(([^()]+)\)\s*$/);
  return m ? m[1].trim() : key;
}

function parseScores(json: string | null): Record<string, number> {
  if (!json) return {};
  try {
    const parsed = JSON.parse(json);
    if (parsed && typeof parsed === 'object')
      return parsed as Record<string, number>;
  } catch {
    // ignore malformed JSON
  }
  return {};
}

const SingleQuestionnaireGrid: React.FC<
  IGridProps & { questionnaireID: number }
> = ({
  scenario,
  slot,
  rows,
  questionnaireID,
  onEditDay,
  onDeleteDay,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const create = useCreateQuestionnaireEntry(scenario.id);
  const update = useUpdateQuestionnaireEntry(scenario.id);
  const del = useDeleteQuestionnaireEntry(scenario.id);

  const { data: questionnaire } =
    useGetQuestionnairesByIdParam(questionnaireID);

  const findEntry = (row: IDayRow): IScenarioQuestionnaireEntry | undefined =>
    slot.questionnaireEntries.find(
      (e) =>
        sameDay({ offsetDays: e.offsetDays, timeOfDay: e.timeOfDay }, row) &&
        e.questionnaireID === questionnaireID,
    );

  // Canonical subscales are extracted from the questionnaire JSON (same source the
  // legacy importer uses in questionnaireProcessor.js): unique categoryEn from
  // questionScores, plus categoryEn from categoryConfigs (which provides "Total" etc.).
  const canonicalKeys = useMemo<string[]>(() => {
    if (!questionnaire) return [];
    const ordered: string[] = [];
    const seen = new Set<string>();
    const add = (label: string | null | undefined) => {
      const v = (label ?? '').trim();
      if (!v || seen.has(v)) return;
      seen.add(v);
      ordered.push(v);
    };
    for (const q of questionnaire.questionScores ?? []) add(q.categoryEn);
    for (const c of questionnaire.categoryConfigs ?? []) add(c.categoryEn);
    return ordered;
  }, [questionnaire]);

  // Legacy / external keys present in stored scoresJson that are NOT canonical
  // (e.g. older converter output like "WEISS-P (Risky)").
  const extraneousKeys = useMemo<string[]>(() => {
    const set = new Set<string>();
    for (const e of slot.questionnaireEntries) {
      if (e.questionnaireID !== questionnaireID) continue;
      for (const k of Object.keys(parseScores(e.scoresJson))) set.add(k);
    }
    for (const k of canonicalKeys) set.delete(k);
    return [...set];
  }, [slot.questionnaireEntries, questionnaireID, canonicalKeys]);

  const allKeys = useMemo(
    () => [...canonicalKeys, ...extraneousKeys],
    [canonicalKeys, extraneousKeys],
  );

  const upsertCell = async (
    row: IDayRow,
    scoreKey: string,
    value: number | null,
  ) => {
    const existing = findEntry(row);
    const scores = parseScores(existing?.scoresJson ?? null);
    if (value === null) delete scores[scoreKey];
    else scores[scoreKey] = value;
    const empty = Object.keys(scores).length === 0;

    if (empty && !existing?.answersJson) {
      if (existing) await del.mutateAsync(existing.id);
      return;
    }
    const model: IScenarioQuestionnaireEntryWrite = {
      questionnaireID,
      offsetDays: row.offsetDays,
      timeOfDay: row.timeOfDay,
      scoresJson: empty ? null : JSON.stringify(scores),
      answersJson: existing?.answersJson ?? null,
    };
    if (existing) await update.mutateAsync({ entryId: existing.id, model });
    else await create.mutateAsync({ slotId: slot.id, model });
  };

  if (!questionnaire)
    {return (
      <Text c="dimmed" size="sm">
        {t('common.loading')}
      </Text>
    );}

  return (
    <Stack gap="sm">
      <Table withTableBorder withColumnBorders verticalSpacing="xs">
        <Table.Thead>
          <Table.Tr>
            <Table.Th style={{ width: '1%', whiteSpace: 'nowrap' }}>
              {t('scenario.col.day')}
            </Table.Th>
            {allKeys.map((k) => (
              <Table.Th key={k} style={{ width: '1%', whiteSpace: 'nowrap' }}>
                <Text fz="xs" title={k}>
                  {subscaleLabel(k)}
                </Text>
              </Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row, idx) => {
            const entry = findEntry(row);
            const scores = parseScores(entry?.scoresJson ?? null);
            const answerBacked = !!entry?.answersJson;
            return (
              <Table.Tr key={dayKey(row)}>
                <Table.Td style={{ width: '1%', whiteSpace: 'nowrap' }}>
                  <Group gap={4} wrap="nowrap">
                    <DayRowHeader
                      rank={idx + 1}
                      row={row}
                      onEditDay={(o, tod) => onEditDay(row, o, tod)}
                      onDeleteDay={() => onDeleteDay(row)}
                      readOnly={readOnly}
                    />
                    {answerBacked && (
                      <Badge size="xs" variant="light">
                        {t('scenario.answersIndicator')}
                      </Badge>
                    )}
                  </Group>
                </Table.Td>
                {allKeys.map((k) => (
                  <Table.Td key={k} style={{ padding: 2 }}>
                    <NumericCell
                      value={scores[k] ?? null}
                      compact
                      readOnly={readOnly}
                      onCommit={(next) => upsertCell(row, k, next)}
                    />
                  </Table.Td>
                ))}
              </Table.Tr>
            );
          })}
          {rows.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={allKeys.length + 1}>
                <Text c="dimmed" size="sm">
                  {t('scenario.noEntries')}
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
    </Stack>
  );
};

// ---------- Physiological grid ----------

const PhysiologicalGrid: React.FC<IGridProps> = ({
  scenario,
  slot,
  rows,
  onEditDay,
  onDeleteDay,
}) => {
  const { t } = useTranslation();
  const { data: lookups } = useScenarioLookups();
  const create = useCreatePhysiologicalEntry(scenario.id);
  const update = useUpdatePhysiologicalEntry(scenario.id);
  const del = useDeletePhysiologicalEntry(scenario.id);

  const columns = useMemo(() => lookups?.physiologicalTypes ?? [], [lookups]);

  const findEntry = (
    row: IDayRow,
    typeID: number,
  ): IScenarioPhysiologicalEntry | undefined =>
    slot.physiologicalEntries.find(
      (e) =>
        sameDay({ offsetDays: e.offsetDays, timeOfDay: e.timeOfDay }, row) &&
        e.physiologicalTypeID === typeID,
    );

  const upsert = async (row: IDayRow, typeID: number, value: number | null) => {
    const existing = findEntry(row, typeID);
    if (value === null) {
      if (existing) await del.mutateAsync(existing.id);
      return;
    }
    const model: IScenarioPhysiologicalEntryWrite = {
      physiologicalTypeID: typeID,
      unitID: existing?.unitID ?? null,
      offsetDays: row.offsetDays,
      timeOfDay: row.timeOfDay,
      value,
    };
    if (existing) await update.mutateAsync({ entryId: existing.id, model });
    else await create.mutateAsync({ slotId: slot.id, model });
  };

  return (
    <Stack gap="sm">
      <Title order={5}>{t('scenario.physiological')}</Title>
      <Table withTableBorder withColumnBorders verticalSpacing="xs">
        <Table.Thead>
          <Table.Tr>
            <Table.Th style={{ width: '1%', whiteSpace: 'nowrap' }}>
              {t('scenario.col.day')}
            </Table.Th>
            {columns.map((c) => (
              <Table.Th key={c.id}>
                <Text fz="sm">{c.name}</Text>
              </Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row, idx) => (
            <Table.Tr key={dayKey(row)}>
              <Table.Td style={{ width: '1%', whiteSpace: 'nowrap' }}>
                <DayRowHeader
                  rank={idx + 1}
                  row={row}
                  onEditDay={(o, tod) => onEditDay(row, o, tod)}
                  onDeleteDay={() => onDeleteDay(row)}
                />
              </Table.Td>
              {columns.map((c) => {
                const entry = findEntry(row, c.id);
                return (
                  <Table.Td key={c.id}>
                    <NumericCell
                      value={entry?.value ?? null}
                      onCommit={(next) => upsert(row, c.id, next)}
                    />
                  </Table.Td>
                );
              })}
            </Table.Tr>
          ))}
          {rows.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={Math.max(columns.length, 0) + 1}>
                <Text c="dimmed" size="sm">
                  {t('scenario.noEntries')}
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
    </Stack>
  );
};

// ---------- Journal section: sub-tabs per journal ----------

interface IJournalGridProps extends IGridProps {
  lang: string;
}

interface IJournalResponseValue {
  journalQuestionID: number;
  responseValue?: string;
  subResponseValue?: string;
  isSkipped?: boolean;
}

function parseJournalResponses(json: string | null): IJournalResponseValue[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (r) =>
        r && typeof r === 'object' && typeof r.journalQuestionID === 'number',
    );
  } catch {
    return [];
  }
}

function parseQuestionOptions(json: string | null | undefined): any {
  if (!json) return null;
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}

const JournalGrid: React.FC<IJournalGridProps> = ({
  scenario,
  slot,
  rows,
  lang,
  onEditDay,
  onDeleteDay,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const [activeJID, setActiveJID] = useState<string | null>(
    scenario.journals[0]?.journalID?.toString() ?? null,
  );

  useEffect(() => {
    if (!activeJID && scenario.journals[0])
      setActiveJID(scenario.journals[0].journalID.toString());
  }, [scenario.journals, activeJID]);

  if (scenario.journals.length === 0)
    {return (
      <Text c="dimmed" size="sm">
        {t('scenario.noTypesSelectedHint')}
      </Text>
    );}

  return (
    <Stack gap="sm">
      <Title order={5}>{t('scenario.journals')}</Title>
      <Tabs value={activeJID} onChange={setActiveJID} variant="outline">
        <Tabs.List>
          {scenario.journals.map((j) => (
            <Tabs.Tab key={j.id} value={j.journalID.toString()}>
              {lang === 'fr' ? j.nameFr : j.nameEn}
            </Tabs.Tab>
          ))}
        </Tabs.List>
        {scenario.journals.map((j) => (
          <Tabs.Panel key={j.id} value={j.journalID.toString()} pt="md">
            <SingleJournalGrid
              scenario={scenario}
              slot={slot}
              rows={rows}
              journalID={j.journalID}
              lang={lang}
              onEditDay={onEditDay}
              onDeleteDay={onDeleteDay}
              readOnly={readOnly}
            />
          </Tabs.Panel>
        ))}
      </Tabs>
    </Stack>
  );
};

const SingleJournalGrid: React.FC<
  IGridProps & { journalID: number; lang: string }
> = ({
  scenario,
  slot,
  rows,
  journalID,
  lang,
  onEditDay,
  onDeleteDay,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const create = useCreateJournalEntry(scenario.id);
  const update = useUpdateJournalEntry(scenario.id);
  const del = useDeleteJournalEntry(scenario.id);

  const { data: journal } = useJournal(journalID);

  const orderedQuestions = useMemo<IJournalQuestion[]>(() => {
    if (!journal) return [];
    const cats = [...(journal.categories ?? [])].sort(
      (a, b) => a.order - b.order,
    );
    return cats.flatMap((c) =>
      [...(c.questions ?? [])].sort((a, b) => a.order - b.order),
    );
  }, [journal]);

  const findEntry = (row: IDayRow): IScenarioJournalEntry | undefined =>
    slot.journalEntries.find(
      (e) =>
        sameDay({ offsetDays: e.offsetDays, timeOfDay: e.timeOfDay }, row) &&
        e.journalID === journalID,
    );

  const upsertCell = async (
    row: IDayRow,
    questionId: number,
    next: Partial<IJournalResponseValue> | null,
  ) => {
    const existing = findEntry(row);
    const responses = parseJournalResponses(existing?.responsesJson ?? null);
    const idx = responses.findIndex((r) => r.journalQuestionID === questionId);

    if (next === null) {
      if (idx >= 0) responses.splice(idx, 1);
    } else {
      const merged: IJournalResponseValue = {
        journalQuestionID: questionId,
        ...(idx >= 0 ? responses[idx] : {}),
        ...next,
      };
      if (idx >= 0) responses[idx] = merged;
      else responses.push(merged);
    }

    const empty = responses.length === 0;
    if (existing) {
      if (empty) 
        await del.mutateAsync(existing.id);
       else {
        const model: IScenarioJournalEntryWrite = {
          journalID,
          offsetDays: row.offsetDays,
          timeOfDay: row.timeOfDay,
          responsesJson: JSON.stringify(responses),
        };
        await update.mutateAsync({ entryId: existing.id, model });
      }
    } else if (!empty) {
      const model: IScenarioJournalEntryWrite = {
        journalID,
        offsetDays: row.offsetDays,
        timeOfDay: row.timeOfDay,
        responsesJson: JSON.stringify(responses),
      };
      await create.mutateAsync({ slotId: slot.id, model });
    }
  };

  if (!journal)
    {return (
      <Text c="dimmed" size="sm">
        {t('common.loading')}
      </Text>
    );}

  if (orderedQuestions.length === 0)
    {return (
      <Text c="dimmed" size="sm">
        {t('scenario.noEntries')}
      </Text>
    );}

  return (
    <Stack gap="sm">
      <Table withTableBorder withColumnBorders verticalSpacing="xs">
        <Table.Thead>
          <Table.Tr>
            <Table.Th style={{ width: '1%', whiteSpace: 'nowrap' }}>
              {t('scenario.col.day')}
            </Table.Th>
            {orderedQuestions.map((q) => (
              <Table.Th key={q.id}>
                <Text
                  fz="xs"
                  title={lang === 'fr' ? q.questionTextFr : q.questionTextEn}
                >
                  {lang === 'fr' ? q.questionTextFr : q.questionTextEn}
                </Text>
              </Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row, idx) => {
            const entry = findEntry(row);
            const responses = parseJournalResponses(
              entry?.responsesJson ?? null,
            );
            return (
              <Table.Tr key={dayKey(row)}>
                <Table.Td style={{ width: '1%', whiteSpace: 'nowrap' }}>
                  <DayRowHeader
                    rank={idx + 1}
                    row={row}
                    onEditDay={(o, tod) => onEditDay(row, o, tod)}
                    onDeleteDay={() => onDeleteDay(row)}
                    readOnly={readOnly}
                  />
                </Table.Td>
                {orderedQuestions.map((q) => {
                  const r = responses.find((x) => x.journalQuestionID === q.id);
                  return (
                    <Table.Td key={q.id}>
                      <JournalAnswerCell
                        question={q}
                        response={r}
                        lang={lang}
                        readOnly={readOnly}
                        onCommit={(next) => upsertCell(row, q.id, next)}
                      />
                    </Table.Td>
                  );
                })}
              </Table.Tr>
            );
          })}
          {rows.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={orderedQuestions.length + 1}>
                <Text c="dimmed" size="sm">
                  {t('scenario.noEntries')}
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
    </Stack>
  );
};

const JOURNAL_QUESTION_TYPE = {
  YES_NO: 1,
  YES_NO_WITH_SCALE: 2,
  YES_NO_WITH_COUNT: 3,
  CONDITIONAL_MULTISELECT: 4,
  MULTI_SELECT: 6,
  FREE_TEXT: 7,
  SCALE: 8,
  TOGGLE: 9,
} as const;

const JournalAnswerCell: React.FC<{
  question: IJournalQuestion;
  response: IJournalResponseValue | undefined;
  lang: string;
  readOnly?: boolean;
  onCommit: (next: Partial<IJournalResponseValue> | null) => void;
}> = ({ question, response, lang, readOnly = false, onCommit }) => {
  const { t } = useTranslation();
  const opts = useMemo(
    () => parseQuestionOptions(question.optionsJson),
    [question.optionsJson],
  );
  const value = response?.responseValue ?? '';
  const sub = response?.subResponseValue ?? '';

  if (readOnly) {
    if (response?.isSkipped) return <Text size="xs">—</Text>;
    if (!value && !sub)
      {return (
        <Text size="xs" c="dimmed">
          —
        </Text>
      );}
    const display = sub
      ? `${value || ''}${value && sub ? ' · ' : ''}${sub}`
      : value;
    return <Text size="xs">{display}</Text>;
  }

  const yesNoData = [
    { value: 'yes', label: t('journal.yes') },
    { value: 'no', label: t('journal.no') },
  ];

  switch (question.questionTypeID) {
    case JOURNAL_QUESTION_TYPE.YES_NO:
      return (
        <Select
          size="xs"
          data={yesNoData}
          value={value || null}
          onChange={(v) => onCommit(v === null ? null : { responseValue: v })}
          clearable
          placeholder="—"
          styles={{ input: { minWidth: 90 } }}
        />
      );

    case JOURNAL_QUESTION_TYPE.YES_NO_WITH_SCALE:
    case JOURNAL_QUESTION_TYPE.YES_NO_WITH_COUNT:
      return (
        <Group gap={4} wrap="nowrap">
          <Select
            size="xs"
            data={yesNoData}
            value={value || null}
            onChange={(v) => {
              if (v === null) onCommit(null);
              else if (v === 'no')
                onCommit({ responseValue: v, subResponseValue: '' });
              else onCommit({ responseValue: v });
            }}
            clearable
            placeholder="—"
            styles={{ input: { minWidth: 80 } }}
          />
          <NumericCellInline
            value={sub === '' ? null : Number(sub)}
            disabled={value !== 'yes'}
            onCommit={(n) =>
              onCommit({ subResponseValue: n === null ? '' : String(n) })
            }
          />
        </Group>
      );

    case JOURNAL_QUESTION_TYPE.MULTI_SELECT: {
      const ms = (opts ?? {}) as IMultiSelectOptions;
      const data = (ms.options ?? []).map((o: any) => ({
        value: o.valueEn,
        label: lang === 'fr' ? o.valueFr : o.valueEn,
      }));
      const allowMultiple = ms.allowMultiple === true;
      if (allowMultiple) {
        const selected = value ? value.split(',').filter(Boolean) : [];
        return (
          <MultiSelect
            size="xs"
            data={data}
            value={selected}
            onChange={(vals) =>
              onCommit(
                vals.length === 0 ? null : { responseValue: vals.join(',') },
              )
            }
            clearable
            searchable
            placeholder="—"
            styles={{ input: { minWidth: 160 } }}
          />
        );
      }
      return (
        <Select
          size="xs"
          data={data}
          value={value || null}
          onChange={(v) => onCommit(v === null ? null : { responseValue: v })}
          clearable
          searchable
          placeholder="—"
          styles={{ input: { minWidth: 140 } }}
        />
      );
    }

    case JOURNAL_QUESTION_TYPE.FREE_TEXT:
      return (
        <TextCell
          value={value || null}
          onCommit={(next) =>
            onCommit(next === null ? null : { responseValue: next })
          }
        />
      );

    case JOURNAL_QUESTION_TYPE.SCALE: {
      const scale = (opts ?? {}) as {
        min?: number;
        max?: number;
        step?: number;
      };
      return (
        <NumericCellInline
          value={value === '' ? null : Number(value)}
          min={scale.min}
          max={scale.max}
          step={scale.step}
          onCommit={(n) =>
            onCommit(n === null ? null : { responseValue: String(n) })
          }
        />
      );
    }

    case JOURNAL_QUESTION_TYPE.TOGGLE:
      return (
        <Select
          size="xs"
          data={[
            { value: 'on', label: t('journal.yes') },
            { value: '', label: t('journal.no') },
          ]}
          value={value === 'on' ? 'on' : value === '' && response ? '' : null}
          onChange={(v) =>
            onCommit(v === null ? null : { responseValue: v ?? '' })
          }
          clearable
          placeholder="—"
          styles={{ input: { minWidth: 90 } }}
        />
      );

    case JOURNAL_QUESTION_TYPE.CONDITIONAL_MULTISELECT:
      return (
        <Group gap={4} wrap="nowrap">
          <TextCell
            value={value || null}
            placeholder={t('scenario.col.primary') ?? 'primary'}
            onCommit={(next) =>
              onCommit(next === null ? null : { responseValue: next })
            }
          />
          <TextCell
            value={sub || null}
            placeholder={t('scenario.col.secondary') ?? 'secondary'}
            onCommit={(next) => onCommit({ subResponseValue: next ?? '' })}
          />
        </Group>
      );

    default:
      return (
        <TextCell
          value={value || null}
          onCommit={(next) =>
            onCommit(next === null ? null : { responseValue: next })
          }
        />
      );
  }
};

const NumericCellInline: React.FC<{
  value: number | null;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  onCommit: (next: number | null) => void;
}> = ({ value, min, max, step, disabled, onCommit }) => {
  const [local, setLocal] = useState<string>(
    value === null || value === undefined ? '' : value.toString(),
  );

  useEffect(() => {
    setLocal(value === null || value === undefined ? '' : value.toString());
  }, [value]);

  const commit = () => {
    const trimmed = local.trim();
    if (trimmed === '') {
      if (value !== null && value !== undefined) onCommit(null);
      return;
    }
    const n = Number(trimmed);
    if (Number.isNaN(n)) {
      setLocal(value === null || value === undefined ? '' : value.toString());
      return;
    }
    if (min !== undefined && n < min) return;
    if (max !== undefined && n > max) return;
    if (n !== value) onCommit(n);
  };

  return (
    <TextInput
      value={local}
      onChange={(e) => setLocal(e.currentTarget.value)}
      onBlur={commit}
      disabled={disabled}
      placeholder={
        min !== undefined && max !== undefined ? `${min}-${max}` : undefined
      }
      size="xs"
      styles={{ input: { textAlign: 'right', minWidth: 70 } }}
      step={step}
    />
  );
};

// ---------- Clinical note grid ----------

const ClinicalNoteGrid: React.FC<IGridProps> = ({
  scenario,
  slot,
  rows,
  onEditDay,
  onDeleteDay,
}) => {
  const { t } = useTranslation();
  const create = useCreateClinicalNoteEntry(scenario.id);
  const update = useUpdateClinicalNoteEntry(scenario.id);
  const del = useDeleteClinicalNoteEntry(scenario.id);

  const columns = scenario.clinicalNoteTypes;

  const [editing, setEditing] = useState<{
    entry: IScenarioClinicalNoteEntry | null;
    col: { noteTypeID: ClinicalNoteType; questionnaireID: number | null };
    offsetDays: number;
    timeOfDay: string | null;
  } | null>(null);

  if (columns.length === 0)
    {return (
      <Text c="dimmed" size="sm">
        {t('scenario.noTypesSelectedHint')}
      </Text>
    );}

  const colKey = (c: (typeof columns)[number]) =>
    `${c.noteTypeID}::${c.questionnaireID ?? ''}`;

  const findEntry = (
    row: IDayRow,
    noteTypeID: ClinicalNoteType,
    questionnaireID: number | null,
  ): IScenarioClinicalNoteEntry | undefined =>
    slot.clinicalNoteEntries.find(
      (e) =>
        sameDay({ offsetDays: e.offsetDays, timeOfDay: e.timeOfDay }, row) &&
        e.noteTypeID === noteTypeID &&
        (e.questionnaireID ?? null) === (questionnaireID ?? null),
    );

  const noteTypeLabel = (n: ClinicalNoteType) => {
    switch (n) {
      case ClinicalNoteType.PhysicalExamination:
        return t('scenario.noteType.physicalExamination');
      case ClinicalNoteType.MedicalHistory:
        return t('scenario.noteType.medicalHistory');
      case ClinicalNoteType.Counselling:
        return t('scenario.noteType.counselling');
    }
  };

  const onSubmit = async (model: IScenarioClinicalNoteEntryWrite) => {
    if (!editing) return;
    if (editing.entry)
      await update.mutateAsync({ entryId: editing.entry.id, model });
    else await create.mutateAsync({ slotId: slot.id, model });

    setEditing(null);
  };

  const previewEntry = (e: IScenarioClinicalNoteEntry | undefined) => {
    if (!e) return '';
    const parts: string[] = [];
    if (e.freeTextNote) parts.push(e.freeTextNote.slice(0, 30));
    if (e.answersJson) parts.push('JSON');
    return parts.join(' · ');
  };

  return (
    <Stack gap="sm">
      <Title order={5}>{t('scenario.clinicalNotes')}</Title>
      <Table withTableBorder withColumnBorders verticalSpacing="xs">
        <Table.Thead>
          <Table.Tr>
            <Table.Th style={{ width: '1%', whiteSpace: 'nowrap' }}>
              {t('scenario.col.day')}
            </Table.Th>
            {columns.map((c) => (
              <Table.Th key={colKey(c)}>
                {noteTypeLabel(c.noteTypeID)}
                {c.questionnaireShortNameEn
                  ? ` · ${c.questionnaireShortNameEn}`
                  : ''}
              </Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row, idx) => (
            <Table.Tr key={dayKey(row)}>
              <Table.Td style={{ width: '1%', whiteSpace: 'nowrap' }}>
                <DayRowHeader
                  rank={idx + 1}
                  row={row}
                  onEditDay={(o, tod) => onEditDay(row, o, tod)}
                  onDeleteDay={() => onDeleteDay(row)}
                />
              </Table.Td>
              {columns.map((c) => {
                const entry = findEntry(row, c.noteTypeID, c.questionnaireID);
                return (
                  <Table.Td key={colKey(c)}>
                    <Group gap={4} wrap="nowrap">
                      <Text
                        size="xs"
                        c={entry ? undefined : 'dimmed'}
                        truncate
                        maw={140}
                      >
                        {entry ? previewEntry(entry) : '—'}
                      </Text>
                      <ActionIcon
                        size="sm"
                        variant="subtle"
                        onClick={() =>
                          setEditing({
                            entry: entry ?? null,
                            col: {
                              noteTypeID: c.noteTypeID,
                              questionnaireID: c.questionnaireID,
                            },
                            offsetDays: row.offsetDays,
                            timeOfDay: row.timeOfDay,
                          })
                        }
                      >
                        <IconEdit size={14} />
                      </ActionIcon>
                      {entry && (
                        <ActionIcon
                          size="sm"
                          variant="subtle"
                          color="red"
                          onClick={() => del.mutate(entry.id)}
                        >
                          <IconTrash size={14} />
                        </ActionIcon>
                      )}
                    </Group>
                  </Table.Td>
                );
              })}
            </Table.Tr>
          ))}
          {rows.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={columns.length + 1}>
                <Text c="dimmed" size="sm">
                  {t('scenario.noEntries')}
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>

      {editing && (
        <ClinicalNoteCellModal
          state={editing}
          onClose={() => setEditing(null)}
          onSubmit={onSubmit}
        />
      )}
    </Stack>
  );
};

const ClinicalNoteCellModal: React.FC<{
  state: {
    entry: IScenarioClinicalNoteEntry | null;
    col: { noteTypeID: ClinicalNoteType; questionnaireID: number | null };
    offsetDays: number;
    timeOfDay: string | null;
  };
  onClose: () => void;
  onSubmit: (model: IScenarioClinicalNoteEntryWrite) => Promise<void>;
}> = ({ state, onClose, onSubmit }) => {
  const { t } = useTranslation();
  const [freeTextNote, setFreeTextNote] = useState<string>(
    state.entry?.freeTextNote ?? '',
  );
  const [answersJson, setAnswersJson] = useState<string>(
    state.entry?.answersJson ?? '',
  );

  return (
    <Modal
      opened
      onClose={onClose}
      title={state.entry ? t('scenario.editEntry') : t('scenario.addEntry')}
    >
      <Stack>
        <Textarea
          label={t('scenario.col.freeText')}
          value={freeTextNote}
          onChange={(e) => setFreeTextNote(e.currentTarget.value)}
          autosize
          minRows={2}
        />
        <Textarea
          label={t('scenario.col.answersJson')}
          value={answersJson}
          onChange={(e) => setAnswersJson(e.currentTarget.value)}
          autosize
          minRows={3}
        />
        <Group justify="flex-end">
          <Button variant="outline" onClick={onClose}>
            {t('button.cancel')}
          </Button>
          <Button
            onClick={() =>
              onSubmit({
                noteTypeID: state.col.noteTypeID,
                questionnaireID: state.col.questionnaireID,
                offsetDays: state.offsetDays,
                timeOfDay: state.timeOfDay,
                freeTextNote: freeTextNote || null,
                answersJson: answersJson || null,
              })
            }
          >
            {t('button.save')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

// ---------- Life events grid (read-only — managed via Excel import) ----------

const LifeEventGrid: React.FC<{
  slot: IScenarioPatientSlot;
  rows: IDayRow[];
}> = ({ slot, rows }) => {
  const { t } = useTranslation();

  const entriesForRow = (row: IDayRow): IScenarioLifeEventEntry[] =>
    (slot.lifeEventEntries ?? []).filter((e) =>
      sameDay({ offsetDays: e.offsetDays, timeOfDay: e.timeOfDay }, row),
    );

  return (
    <Stack gap="sm">
      <Title order={5}>{t('scenario.lifeEvents')}</Title>
      <Table
        withTableBorder
        withColumnBorders
        verticalSpacing="xs"
        style={{ tableLayout: 'fixed', width: '100%', maxWidth: 1000 }}
      >
        <Table.Thead>
          <Table.Tr>
            <Table.Th style={{ width: 120, whiteSpace: 'nowrap' }}>
              {t('scenario.col.day')}
            </Table.Th>
            <Table.Th style={{ width: 220 }}>
              {t('scenario.col.title')}
            </Table.Th>
            <Table.Th>{t('scenario.col.description')}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.flatMap((row, idx) => {
            const entries = entriesForRow(row);
            if (entries.length === 0) return [];
            return entries.map((entry, j) => (
              <Table.Tr key={`${dayKey(row)}-${entry.id}`}>
                {j === 0 ? (
                  <Table.Td
                    rowSpan={entries.length}
                    style={{ width: '1%', whiteSpace: 'nowrap' }}
                  >
                    <DayRowHeader
                      rank={idx + 1}
                      row={row}
                      onEditDay={() => undefined}
                      onDeleteDay={() => undefined}
                      readOnly
                    />
                  </Table.Td>
                ) : null}
                <Table.Td style={{ verticalAlign: 'top' }}>
                  <Text
                    size="sm"
                    fw={500}
                    style={{
                      whiteSpace: 'pre-wrap',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {entry.title}
                  </Text>
                </Table.Td>
                <Table.Td style={{ verticalAlign: 'top' }}>
                  <Text
                    size="sm"
                    c={entry.description ? undefined : 'dimmed'}
                    style={{
                      whiteSpace: 'pre-wrap',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {entry.description ?? '—'}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ));
          })}
          {(slot.lifeEventEntries ?? []).length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={3}>
                <Text c="dimmed" size="sm">
                  {t('scenario.noEntries')}
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
    </Stack>
  );
};

// ---------- Documents grid (read-only — metadata stubs from Excel import) ----------

const DocumentGrid: React.FC<{
  slot: IScenarioPatientSlot;
  rows: IDayRow[];
}> = ({ slot, rows }) => {
  const { t } = useTranslation();

  const entriesForRow = (row: IDayRow): IScenarioDocumentEntry[] =>
    (slot.documentEntries ?? []).filter((e) =>
      sameDay({ offsetDays: e.offsetDays, timeOfDay: e.timeOfDay }, row),
    );

  return (
    <Stack gap="sm">
      <Title order={5}>{t('scenario.documents')}</Title>
      <Text size="xs" c="dimmed">
        {t('scenario.documentStubHint')}
      </Text>
      <Table
        withTableBorder
        withColumnBorders
        verticalSpacing="xs"
        style={{ tableLayout: 'fixed', width: '100%', maxWidth: 1000 }}
      >
        <Table.Thead>
          <Table.Tr>
            <Table.Th style={{ width: 120, whiteSpace: 'nowrap' }}>
              {t('scenario.col.day')}
            </Table.Th>
            <Table.Th style={{ width: 260 }}>
              {t('scenario.col.documentName')}
            </Table.Th>
            <Table.Th>{t('scenario.col.filename')}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.flatMap((row, idx) => {
            const entries = entriesForRow(row);
            if (entries.length === 0) return [];
            return entries.map((entry, j) => (
              <Table.Tr key={`${dayKey(row)}-${entry.id}`}>
                {j === 0 ? (
                  <Table.Td
                    rowSpan={entries.length}
                    style={{ width: '1%', whiteSpace: 'nowrap' }}
                  >
                    <DayRowHeader
                      rank={idx + 1}
                      row={row}
                      onEditDay={() => undefined}
                      onDeleteDay={() => undefined}
                      readOnly
                    />
                  </Table.Td>
                ) : null}
                <Table.Td style={{ verticalAlign: 'top' }}>
                  <Text
                    size="sm"
                    fw={500}
                    style={{
                      whiteSpace: 'pre-wrap',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {entry.documentName}
                  </Text>
                </Table.Td>
                <Table.Td style={{ verticalAlign: 'top' }}>
                  <Text
                    size="sm"
                    c={entry.filename ? undefined : 'dimmed'}
                    style={{
                      whiteSpace: 'pre-wrap',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {entry.filename ?? '—'}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ));
          })}
          {(slot.documentEntries ?? []).length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={3}>
                <Text c="dimmed" size="sm">
                  {t('scenario.noEntries')}
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
    </Stack>
  );
};

// ---------- Prescriptions grid (read-only — populated by Excel import) ----------

const PrescriptionGrid: React.FC<{
  slot: IScenarioPatientSlot;
}> = ({ slot }) => {
  const { t } = useTranslation();

  const entries: IScenarioPrescriptionEntry[] = useMemo(
    () =>
      [...(slot.prescriptionEntries ?? [])].sort(
        (a, b) => a.startOffsetDays - b.startOffsetDays,
      ),
    [slot.prescriptionEntries],
  );

  return (
    <Stack gap="sm">
      <Title order={5}>{t('scenario.prescriptions')}</Title>
      <Table
        withTableBorder
        withColumnBorders
        verticalSpacing="xs"
        style={{ tableLayout: 'fixed', width: '100%', maxWidth: 1100 }}
      >
        <Table.Thead>
          <Table.Tr>
            <Table.Th style={{ width: 100 }}>
              {t('scenario.col.startDay')}
            </Table.Th>
            <Table.Th style={{ width: 100 }}>
              {t('scenario.col.endDay')}
            </Table.Th>
            <Table.Th>{t('scenario.col.drugName')}</Table.Th>
            <Table.Th style={{ width: 100 }}>
              {t('scenario.col.dose')}
            </Table.Th>
            <Table.Th style={{ width: 140 }}>
              {t('scenario.col.unit')}
            </Table.Th>
            <Table.Th style={{ width: 200 }}>
              {t('scenario.col.frequency')}
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {entries.map((entry) => (
            <Table.Tr key={entry.id}>
              <Table.Td>{entry.startOffsetDays}</Table.Td>
              <Table.Td
                style={{
                  color: entry.endOffsetDays == null ? '#999' : undefined,
                }}
              >
                {entry.endOffsetDays ?? '—'}
              </Table.Td>
              <Table.Td style={{ overflowWrap: 'anywhere' }}>
                <Text size="sm" fw={500}>
                  {entry.drugName}
                </Text>
              </Table.Td>
              <Table.Td>{entry.dose}</Table.Td>
              <Table.Td>{entry.unitName ?? entry.unitID}</Table.Td>
              <Table.Td>
                {entry.doseFrequencyName ?? entry.doseFrequencyID}
              </Table.Td>
            </Table.Tr>
          ))}
          {entries.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={6}>
                <Text c="dimmed" size="sm">
                  {t('scenario.noEntries')}
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
    </Stack>
  );
};
